<template>
  <v-form ref="form">
    <v-row>
      <v-col cols="12" sm="8" class="pb-0">
        <TopicTitle
          :title="$t('sales.discount-coupon.coupon-event')"
          :subtitle="$t('sales.discount-coupon.coupon-event-description')"
        />
      </v-col>
    </v-row>
    <v-row class="mt-2">
      <v-col cols="12" sm="6" md="4">
        <BaseAutocomplete
          v-model="couponData.productNId"
          :label="$t('sales.discount-coupon.events')"
          :placeholder="$t('sales.discount-coupon.select-events')"
          :items="allEvents"
          :disabled="isMarketplaceCoupon"
          :persistent-placeholder="false"
          @input="clearTicketsField"
          item-text="descricao"
          item-value="idproduto"
          deletable-chips
          multiple
        />
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <BaseAutocomplete
          v-model="couponData.productVariationNId"
          :label="$t('sales.discount-coupon.tickets')"
          :placeholder="$t('sales.discount-coupon.select-tickets')"
          :items="tickets"
          :disabled="isMarketplaceCoupon || hasMultipleEventsSelected"
          :persistent-placeholder="false"
          item-text="descricao_completa"
          item-value="idproduto_variacao"
          deletable-chips
          multiple
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="8" class="pb-0">
        <TopicTitle
          :title="$t('sales.discount-coupon.import-documents-sheet')"
        />
        <div>
          <small>
            {{
              $t("sales.discount-coupon.import-documents-sheet-description")
            }},
            <a href="/files/documentos-exemplo.csv" download>{{
              $t("sales.discount-coupon.click-here")
            }}</a
            >.
          </small>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <v-file-input
          v-model="documents"
          :label="$t('sales.insert-documents-sheet')"
          :placeholder="$t('sales.discount-coupon.import-csv-sheet')"
          :disabled="isDocumentsDisabled"
          @change="handleFileInput"
          @click:clear="clearDocumentsField"
          outlined
          dense
          persistent-placeholder
          prepend-icon=""
          class="rounded-lg"
          accept=".xlsx,.xls,.csv"
          append-icon="mdi-upload"
          color="fourth_darken"
        ></v-file-input>
      </v-col>

      <v-col v-if="documents" cols="12" sm="6" md="4" class="pt-0">
        <p class="documents-label">
          {{ $t("sales.discount-coupon.imported-sheet") }}
        </p>
        <a
          @click="downloadDocumentsSheet"
          class="documents-link d-block text-center"
        >
          {{ $t("sales.discount-coupon.documents") }}.csv
          <v-icon color="primary-darken-1">mdi-download</v-icon>
        </a>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="8" class="pb-0">
        <TopicTitle
          :title="$t('sales.discount-coupon.membership-integration')"
          :subtitle="
            $t('sales.discount-coupon.membership-integration-description')
          "
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <BaseTextField
          v-model="couponData.membershipToken"
          :label="$t('sales.discount-coupon.membership-token')"
          :placeholder="$t('sales.discount-coupon.insert-token')"
          :disabled="isMembershipDisabled"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <BaseTextField
          v-model="couponData.membershipUrl"
          :label="$t('sales.discount-coupon.consult-members-url')"
          :placeholder="$t('sales.discount-coupon.insert-consult-members-url')"
          :disabled="isMembershipDisabled"
        >
          <template v-slot:append-outer>
            <ToolTip
              :text="$t('sales.discount-coupon.consult-members-tooltip')"
            />
          </template>
        </BaseTextField>
      </v-col>
    </v-row>

    <v-row v-if="isAdmin">
      <v-col cols="12" sm="8" class="pb-0">
        <TopicTitle :title="$t('sales.discount-coupon.ecommerce-coupon')" />
        <small
          v-html="$t('sales.discount-coupon.ecommerce-coupon-description')"
        ></small>
      </v-col>
    </v-row>
    <v-row v-if="isAdmin">
      <v-col cols="12" sm="6" md="4">
        <BaseSelect
          v-model="marketplaceCoupon"
          :label="$t('sales.discount-coupon.select-status')"
          :persistent-placeholder="false"
          :items="statusOptions"
          :rules="[rules.required]"
          item-text="text"
          item-value="value"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="8" class="pb-0">
        <TopicTitle
          :title="$t('sales.discount-coupon.coupon-status')"
          :subtitle="$t('sales.discount-coupon.coupon-status-description')"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <BaseSelect
          v-model="couponData.status"
          :label="$t('sales.discount-coupon.select-status')"
          :persistent-placeholder="false"
          :items="statusOptions"
          :rules="[rules.required]"
          item-text="text"
          item-value="value"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { normalizeString } from "../../../utils";
import formRules from "../../../mixins/formRules";
import TopicTitle from "../../TopicTitle.vue";
import BaseAutocomplete from "../../forms/BaseAutocomplete.vue";
import BaseSelect from "../../forms/BaseSelect.vue";
import BaseTextField from "../../forms/BaseTextField.vue";
import ToolTip from "./ToolTip.vue";

export default {
  name: "GeneralSettings",
  mixins: [formRules],
  components: {
    TopicTitle,
    BaseAutocomplete,
    BaseSelect,
    BaseTextField,
    ToolTip,
  },
  data() {
    return {
      normalizeString,
      marketplaceCoupon: "inactive",
      documents: null,
    };
  },
  computed: {
    ...mapGetters(["couponData", "storeSequencial", "isAdmin", "allEvents"]),
    statusOptions() {
      return [
        {
          text: this.$t("common.active"),
          value: "active",
        },
        {
          text: this.$t("common.inactive"),
          value: "inactive",
        },
      ];
    },
    hasMultipleEventsSelected() {
      return this.couponData.productNId?.length > 1;
    },
    tickets() {
      const variations = this.allEvents.find(
        event => event.idproduto == this.couponData.productNId?.[0]
      );

      return variations?.variantes?.variacoes_produto;
    },
    isMarketplaceCoupon() {
      return this.marketplaceCoupon === "active";
    },
    isEdit() {
      return this.$route.path == "/cupom-desconto/editar";
    },
    isMembershipDisabled() {
      return !!this.documents || !!this.couponData.documents;
    },
    isDocumentsDisabled() {
      return (
        !!this.couponData.membershipToken || !!this.couponData.membershipUrl
      );
    },
  },
  watch: {
    marketplaceCoupon() {
      this.couponData.marketplaceCoupon = this.marketplaceCoupon;

      if (this.isMarketplaceCoupon) {
        this.clearEventsField();
        this.clearTicketsField();
      }
    },
  },
  methods: {
    ...mapActions(["fetchEvents"]),
    handleFileInput(file) {
      if (file) {
        const reader = new FileReader();

        reader.onload = event => {
          const convertToArray = event.target.result
            .split("\n")
            .map(item => normalizeString(item))
            .filter(item => item !== "");

          this.couponData.documents = convertToArray.join(";");
        };

        reader.readAsText(file);
      }
    },
    downloadDocumentsSheet() {
      const rows = this.couponData.documents.split(";");
      const csvContent = rows.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "documentos.csv");
      link.click();
    },
    convertArrayToFile(array) {
      const rows = array.split(";");
      const csvContent = rows.join("\n");

      return new File([csvContent], "documentos.csv", {
        type: "text/csv;charset=utf-8;",
      });
    },
    clearDocumentsField() {
      this.couponData.documents = null;
      this.documents = null;
    },
    clearEventsField() {
      this.couponData.productNId = [];
    },
    clearTicketsField() {
      this.couponData.productVariationNId = [];
    },
  },
  mounted() {
    if (!this.isEdit) {
      this.couponData.status = "active";
    } else {
      this.marketplaceCoupon = this.couponData.marketplaceCoupon;
    }

    if (this.isEdit && this.couponData.documents) {
      const file = this.convertArrayToFile(this.couponData.documents);
      this.documents = file;
    }

    this.fetchEvents({
      producerId: this.storeSequencial,
      filtro: [],
    });
  },
};
</script>

<style lang="scss" scoped>
.documents-label {
  color: #767579;
  font-size: 14px;
}

.documents-link {
  text-decoration: none;
  color: #572c88 !important;
}

.documents-link:hover {
  text-decoration: underline;
}
</style>
